import { ReactElement, useEffect, useState } from "react";
import { IParentSize } from "../../../../types";
import {
  IAxisBand,
  IAxisLinear,
  ILineChartConfig, IPieConfig, IPoint,
  ISeriesWithAxes,
  IStackerAreaChartConfig
} from "../../../../components/charts/types";
import { Paper } from "@mui/material";
import LineChart from "../../../../components/charts/lineChart";
import StackedLineChart from "../../../../components/charts/stackedLineChart";
import PieChart from "../../../../components/charts/pieChart";

interface IProps {
  hidden: boolean;
  data: Record<string, number | string>[];
  parentSize: IParentSize;
}


const ChartsComponents = ({ hidden, data, parentSize }: IProps): ReactElement => {
  const height = parentSize.height - 10;
  const chartHeight = height / 2 - 24;
  const [ lineChartConfig, setLineChartConfig ] = useState<ILineChartConfig | null>(null);
  const [ stackerAreaChartConfig, setStackerAreaChartConfig ] = useState<IStackerAreaChartConfig | null>(null);
  const [ pieChartConfig, setPieChartConfig ] = useState<IPieConfig | null>(null);

  useEffect(() => {
    if (data) {
      const xAxis: IAxisBand = {
        name: "Reference",
        categories: data.map(x => x.reference.toString()),
      };

      const series: ISeriesWithAxes[] = [
        {
          name: "Power OFF",
          xAxisName: "Reference",
          yAxisName: "Time",
          value: [],
          color: "#e53935",
          unit: "min"
        },
        {
          name: "Power ON",
          xAxisName: "Reference",
          yAxisName: "Time",
          value: [],
          color: "#43a047",
          unit: "min"
        }
      ];

      const offSeries: ISeriesWithAxes[] = [
        {
          name: "EAF Operational Delays",
          xAxisName: "Reference",
          yAxisName: "Time",
          value: [],
          color: "#e53935",
          unit: "min"
        },
        {
          name: "Caster Operational Delays",
          xAxisName: "Reference",
          yAxisName: "Time",
          value: [],
          color: "#d81b60",
          unit: "min"
        },
        {
          name: "Mechanical Delays",
          xAxisName: "Reference",
          yAxisName: "Time",
          value: [],
          color: "#f4511e",
          unit: "min"
        },
        {
          name: "Electrical Delays",
          xAxisName: "Reference",
          yAxisName: "Time",
          value: [],
          color: "#fb8c00",
          unit: "min"
        },
        {
          name: "Refractory Delays",
          xAxisName: "Reference",
          yAxisName: "Time",
          value: [],
          color: "#fdd835",
          unit: "min"
        }
      ];

      data.forEach(record => {
        (series[0].value as IPoint[]).push({ x: record.reference.toString(), y: record["Power OFF"] as number, limits: [] });
        (series[1].value as IPoint[]).push({ x: record.reference.toString(), y: record["Power ON"] as number, limits: [] });

        (offSeries[0].value as IPoint[]).push({ x: record.reference.toString(), y: (record["eaf-operational"] ?? 0) as number, limits: [] });
        (offSeries[1].value as IPoint[]).push({ x: record.reference.toString(), y: (record["caster-operational"] ?? 0) as number, limits: [] });
        (offSeries[2].value as IPoint[]).push({ x: record.reference.toString(), y: (record["eaf-mechanical"] ?? 0) as number, limits: [] });
        (offSeries[3].value as IPoint[]).push({ x: record.reference.toString(), y: (record["eaf-electrical"] ?? 0) as number, limits: [] });
        (offSeries[4].value as IPoint[]).push({ x: record.reference.toString(), y: (record["eaf refractory-operational"] ?? 0) as number, limits: [] });
      });

      setLineChartConfig({
        height: chartHeight,
        xAxis,
        yAxis: [{
          name: "Y",
          minimum: 0,
          maximum: 100,
          ticksCount: 5
        }],
        series: [],
        showLegend: true,
        pointStyle: {
          radius: 4
        }
      });

      setStackerAreaChartConfig({
        height: chartHeight,
        series,
        xAxis,
        yAxis: {
          name: "Time [min]",
          minimum: 0,
          maximum: 100,
          ticksCount: 5
        } as IAxisLinear,
        showLegend: true
      });


      const pieSeries = offSeries.map(s => ({
        ...s,
        value: (s.value as IPoint[]).map(p => p.y).reduce((previousValue, currentValue) => previousValue + currentValue, 0)
      }));

      setPieChartConfig({
        height: chartHeight,
        series: pieSeries
          .filter(x => x.value > 0)
          .sort((a, b) => b.value - a.value),
        padAngle: 0.02,
        innerRadius: 0.2,
        seriesRadius: 1.1,
        showLegend: true,
        sort: "NONE"
      });
    }
  }, [ data ]);

  return (
    <div className="display-grid grid-2x2 p-1" hidden={hidden} style={{ position: "relative", height, maxHeight: height }}>
      <Paper className="p-1" style={{ gridColumn: "1/span 1", gridRow: "1/span 1" }} >
        { lineChartConfig && <LineChart lineChartConfig={lineChartConfig} /> }
      </Paper>

      <Paper className="p-1" style={{ gridColumn: "2/span 1", gridRow: "1/span 1" }}>
        { stackerAreaChartConfig && <StackedLineChart chartConfig={stackerAreaChartConfig} /> }
      </Paper>

      <Paper className="p-2" style={{ gridColumn: "1/span 1", gridRow: "2/span 1" }}>
        { pieChartConfig && <PieChart pieConfig={pieChartConfig} /> }
      </Paper>

      <Paper className="p-2" style={{ gridColumn: "2/span 1", gridRow: "2/span 1" }}>
        { pieChartConfig && <PieChart pieConfig={pieChartConfig} /> }
      </Paper>
    </div>
  );
};

export default ChartsComponents;
